import _ from 'lodash';
import raf from 'raf';
import React, { useState } from 'react';
import { useSelector, batch, useDispatch } from 'react-redux';
import Resizer from 'components/Resizer';
import { ResizerDirection } from 'const';
import { setIsResizingRow, setLastEditedLayoutId } from 'containers/App/actions';
import {
  areActionsDisabled as areActionsDisabledSelector,
  artboardScale,
} from 'containers/App/selectors';
import { updateLayeredRelations } from 'containers/Relations/actions';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { getActiveLayer } from 'hooks/useActiveLayer';
import * as Models from 'models';
import { getMaxRowHeight } from 'utils/relations/getMaxHeight';
import { getMinRowHeight } from 'utils/relations/getMinRowHeight';
import { recalculateRowsHeight } from 'utils/rowsHeight';
import { areThereHorizontalNeighbors } from 'utils/rowsHeight/areThereHorizontalNeighbors';
import { getParent } from 'utils/rowsHeight/getParent';

function getHintMessage(height: number, neighborHeight: number | null): string {
  let message = '';

  if (height) {
    message += `${height} px`;
  }

  if (neighborHeight) {
    message += ` / ${neighborHeight} px`;
  }

  return message;
}

type Props = {
  container: React.MutableRefObject<HTMLElement>;
  height: number;
  layoutId: string;
  relationIsBeingResizedId: string;
  relations: Models.LayeredRelationsMap;
  toggleRelations(relations: Models.LayeredRelationsMap): void;
};

const RowResizer: React.FunctionComponent<Props> = (props) => {
  const {
    container,
    height,
    layoutId,
    relationIsBeingResizedId,
    relations,
    toggleRelations,
  } = props;
  const dispatch = useDispatch();
  const areActionsDisabled = useSelector(areActionsDisabledSelector);
  const scale = useSelector(artboardScale);

  const [finalRelations, setFinalRelations] = useState<Models.LayeredRelationsMap>();

  const activeLayer = getActiveLayer();
  const minHeight = getMinRowHeight(relationIsBeingResizedId, relations, activeLayer);
  const maxHeight = getMaxRowHeight(relationIsBeingResizedId, relations, activeLayer);

  let neighborHeight: number | null = null;
  const { parent, position } = getParent(relationIsBeingResizedId, relations);

  if (areThereHorizontalNeighbors(parent.get('id'), relations)) {
    neighborHeight = parent.getIn(['styles', 'rowsHeight', position + 1], null);
  }

  const onResizeStart = (): void => {
    batch(() => {
      dispatch(saveAppState());
      dispatch(setIsResizingRow(true));
    });
  };

  const onResize = ({ clientY }: { clientY: number }) => raf(() => {
    if (!height || !container.current) {
      return;
    }

    const { bottom } = container.current.getBoundingClientRect();
    const delta = _.round(bottom - clientY);

    const newHeight = _.round(_.clamp(height - delta / scale, minHeight, maxHeight));
    const verifiedDelta = height - newHeight;
    const calculatedFinalRelations = recalculateRowsHeight(
      relationIsBeingResizedId,
      relations,
      verifiedDelta,
      activeLayer,
      true,
      false,
      true,
    );

    setFinalRelations(calculatedFinalRelations);
    toggleRelations(calculatedFinalRelations);
  });

  const onResizeStop = (): void => {
    batch(() => {
      dispatch(setLastEditedLayoutId(layoutId));
      dispatch(updateLayeredRelations(finalRelations || relations));
      dispatch(setIsResizingRow(false));
    });
  };

  return (
    <Resizer
      allowVisibilityChange={!areActionsDisabled}
      scale={scale}
      direction={ResizerDirection.HORIZONTAL}
      hintMessage={getHintMessage(height, neighborHeight)}
      onResize={onResize}
      onResizeStart={onResizeStart}
      onResizeStop={onResizeStop}
    />
  );
};

export default RowResizer;
